import { useRef, useCallback, useState, useEffect, useLayoutEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';
import { useCurrentUser } from '../../AuthComponents';
import { useExperiment, useStudentProgress, usePreLabQuestions, useSubmitPreLabAssessment, useTeacherBypass } from '../../../utils/convexHooks';
import { Id } from '../../../convex/_generated/dataModel';
import PreLabAssessment from '../../PreLabAssessment';
import LabScene from './lab/LabScene';
import RightSidebar from './panels/RightSidebar';
import TutorialOverlay from './tutorial/TutorialOverlay';
import QuizModal from './tutorial/QuizModal';
import ResultsModal from './results/ResultsModal';
import LabAssistant from './lab/LabAssistant';
import LabToolbar from './lab/LabToolbar';
import { useUiStore } from './store/uiStore';
import { useExperimentStore } from './store/experimentStore';
import { useThemeStore } from './store/themeStore';
import { HandTrackerAR, type TrackingLabData } from './lab/HandTrackerAR';
import { GestureProcessor } from './lab/GestureProcessor';
import { HoloOverlay } from './lab/HoloOverlay';
import ARTutorial from './lab/ARTutorial';
import { useExperimentConfig } from './store/experimentConfigStore';
import { ThemeManager } from './lib/ThemeManager';

export default function TitrationLabWrapper() {
  const [searchParams] = useSearchParams();
  const idStr = searchParams.get('id');
  const id = idStr ? (idStr as Id<"experimentConfigs">) : undefined;
  
  const navigate = useNavigate();
  const { user } = useCurrentUser();
  
  // Data hooks
  const experimentConfig = useExperiment(id);
  const progress = useStudentProgress(user?._id, id);
  const preLabQuestions = usePreLabQuestions(id);
  const submitPreLab = useSubmitPreLabAssessment();
  const teacherBypass = useTeacherBypass();
  const setShowQuiz = useUiStore((s) => s.setShowQuiz);
  
  if (!id || !user) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-xl text-gray-700">Missing experiment ID or User</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back</button>
      </div>
    );
  }

  if (experimentConfig === null) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen bg-gray-50">
        <p className="text-xl text-red-600">Experiment not found</p>
        <button onClick={() => navigate('/student/dashboard')} className="mt-4 px-4 py-2 bg-indigo-600 text-white rounded">Back</button>
      </div>
    );
  }

  if (experimentConfig === undefined || progress === undefined || (experimentConfig.preLab.enabled && preLabQuestions === undefined)) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <Loader2 className="w-12 h-12 animate-spin text-indigo-600" />
      </div>
    );
  }

  // Pre-lab logic
  const needsPreLab = experimentConfig.preLab.enabled && progress !== null && 
                      (progress.status === 'not-started' || progress.status === 'pre-lab') &&
                      (!progress.preLab || !progress.preLab.passed);

  if (needsPreLab && preLabQuestions && preLabQuestions.length > 0) {
    // Ensure tutorial / quiz overlays are hidden when the Pre-Lab assessment
    // is shown first so students don't see duplicate pre-lab checks.
    try { setShowQuiz(false); } catch (e) { /* noop */ }
    return (
      <div className="min-h-screen bg-gray-50 py-8 px-4">
        <button 
          onClick={() => navigate('/student/dashboard')}
          className="mb-6 flex flex-row gap-2 items-center text-gray-500 hover:text-indigo-600"
        >
          <ArrowLeft className="w-5 h-5"/> Back to Dashboard
        </button>
        <PreLabAssessment 
          questions={preLabQuestions.map((q: any) => ({ ...q, id: q._id as string }))}
          passingScore={experimentConfig.preLab.passingScore}
          maxAttempts={experimentConfig.preLab.maxAttempts}
          showAnswersAfterFail={experimentConfig.preLab.showAnswersAfterFail}
          allowTeacherBypass={experimentConfig.allowTeacherBypass}
          onPass={async (answers) => {
             try {
                await submitPreLab({
                  experimentConfigId: id,
                  studentId: user._id,
                  answers: answers.map((a: any) => ({
                    questionId: a.questionId as Id<"preLabQuestions">,
                    answer: a.answer
                  }))
                });
             } catch (e: any) {
                alert(e.message || "Failed to save pre-lab progress.");
             }
          }}
          onBypass={async (pwd) => {
             try {
                  await teacherBypass({ experimentConfigId: id, studentId: user._id });
             } catch (e: any) {
                alert(e.message || "Invalid password");
             }
          }}
        />
      </div>
    );
  }

  return <GuidedTitrationHClNaOHBase experimentConfig={experimentConfig} progress={progress} />;
}

function GuidedTitrationHClNaOHBase({ experimentConfig, progress }: { experimentConfig: any, progress: any }) {
    const resetExperiment = useExperimentStore(s => s.resetExperiment);
    const resetUi = useUiStore(s => s.resetUi);

    // Respect the user's saved theme — do NOT force dark mode.
    // Re-apply the current ThemeManager mode so the <html> class is always
    // in sync with what the user chose on the dashboard.
    useLayoutEffect(() => {
        ThemeManager.getInstance().setMode(ThemeManager.getInstance().getMode());
    }, []);

    const setShowTutorial = useUiStore((s) => s.setShowTutorial);
    const setShowAssistant = useUiStore((s) => s.setShowAssistant);
    const setAssistantEnabled = useUiStore((s) => s.setAssistantEnabled);
    const setCurrentConfig = useExperimentConfig((s) => s.setCurrentConfig);
    const setStudentProgress = useExperimentConfig((s) => s.setStudentProgress);

    // Reset experiment state on initial mount
    useEffect(() => {
        resetExperiment();
        resetUi();
    }, [resetExperiment, resetUi]);

    const tutorialInitRef = useRef(false);

    useEffect(() => {
        if (experimentConfig) {
            setCurrentConfig(experimentConfig);
            
            // Only initialize tutorial state once per mount
            if (!tutorialInitRef.current) {
                tutorialInitRef.current = true;
                if (progress?.status === 'completed') {
                    setShowTutorial(false);
                } else if (experimentConfig.showTutorial !== undefined) {
                    setShowTutorial(experimentConfig.showTutorial);
                }
            }

            if (experimentConfig.enableAssistant !== undefined) {
                setAssistantEnabled(experimentConfig.enableAssistant);
                setShowAssistant(experimentConfig.enableAssistant);
            }
        }
        if (progress) {
            setStudentProgress(progress);
        }
    }, [experimentConfig, progress, setShowTutorial, setAssistantEnabled, setShowAssistant, setCurrentConfig, setStudentProgress]);

    const arEnabled = useUiStore((s) => s.arEnabled);
    const resolvedTheme = useThemeStore((s) => s.resolvedTheme);
    const setShowARTutorial = useUiStore((s) => s.setShowARTutorial);
    const [arReady, setArReady] = useState(false);
    const [arStatus, setArStatus] = useState('');
    const arTutorialShownRef = useRef(false);
    const viewResultsInitRef = useRef(false);
    
    const setTitrationData = useExperimentStore((s) => s.setTitrationData);
    const setScore = useExperimentStore((s) => s.setScore);
    const setShowResults = useUiStore((s) => s.setShowResults);

    // Initialize "View Results" state once if loading an already completed experiment
    useEffect(() => {
        if (progress?.status === 'completed' && !viewResultsInitRef.current) {
            viewResultsInitRef.current = true;
            if (progress.labData) {
                if (progress.labData.dataPoints) setTitrationData(progress.labData.dataPoints);
                if (progress.labData.score !== undefined) setScore(progress.labData.score);
                // Reveal the result modal immediately
                setShowResults(true);
            }
        }
    }, [progress, setTitrationData, setScore, setShowResults]);

    useEffect(() => {
        if (arEnabled) { setArReady(false); setArStatus(''); }
    }, [arEnabled]);

    const trackingRef = useRef<TrackingLabData>({
        left: { isPresent: false, isPinching: false, isFist: false, pinchDist: 0, wrist: { x: 0, y: 0, z: 0 }, indexTip: { x: 0, y: 0, z: 0 } },
        right: { isPresent: false, isPinching: false, isFist: false, pinchDist: 0, wrist: { x: 0, y: 0, z: 0 }, indexTip: { x: 0, y: 0, z: 0 } },
        faceYaw: 0,
        facePitch: 0
    });

    const gestureProcessor = useRef(new GestureProcessor());
    const panRef = useRef(0);
    const zoomRef = useRef(0);
    const faceYawRef = useRef(0);
    const facePitchRef = useRef(0);

    const handleTrackingUpdate = useCallback((data: TrackingLabData) => {
        trackingRef.current = data;
        faceYawRef.current = data.faceYaw;

        gestureProcessor.current.processFrame(data, {
            onSwipeLeft: () => {
                const tabs = ['controls', 'data', 'chart', 'exercises'] as const;
                const idx = tabs.indexOf(useUiStore.getState().sidebarTab);
                useUiStore.getState().setSidebarTab(tabs[(idx + tabs.length - 1) % tabs.length]);
            },
            onSwipeRight: () => {
                const tabs = ['controls', 'data', 'chart', 'exercises'] as const;
                const idx = tabs.indexOf(useUiStore.getState().sidebarTab);
                useUiStore.getState().setSidebarTab(tabs[(idx + 1) % tabs.length]);
            },
            onSwipeUp: () => useExperimentStore.getState().addVolume(1.0),
            onSwipeDown: () => useExperimentStore.getState().addVolume(0.1),
            onFaceYaw: (yaw) => { faceYawRef.current = yaw; },
            onFacePitch: (pitch) => { facePitchRef.current = pitch; },
            onZoom: (delta) => { zoomRef.current += delta; },
            onPan: (dx) => { panRef.current += dx; },
        });
    }, []);

    return (
        <div className="flex flex-col h-screen w-full overflow-y-auto lg:overflow-hidden bg-background relative">
            <LabToolbar />
            <TutorialOverlay />
            <QuizModal />
            <ResultsModal />

            <div className="flex flex-col lg:flex-row flex-1 overflow-y-auto lg:overflow-hidden relative w-full h-full bg-background">
                <div className="flex-[2] lg:flex-[3] min-h-[50vh] lg:min-h-0 relative w-full h-full">
                    <LabAssistant />
                    <LabScene
                        panRef={panRef}
                        zoomRef={zoomRef}
                        faceYawRef={faceYawRef}
                        facePitchRef={facePitchRef}
                    />
                </div>
                <RightSidebar />
            </div>

            {arEnabled && (() => {
                const STEPS = [
                    'Downloading Vision engine…',
                    'Loading hand tracking model…',
                    'Loading face tracking model…',
                    'Requesting camera access…',
                    'Camera ready — detecting face…',
                ];
                const currentIdx = STEPS.indexOf(arStatus);

                return (
                    <>
                        <HandTrackerAR
                            onUpdate={handleTrackingUpdate}
                            onCameraReady={() => { }}
                            onCalibrated={() => {
                                setArReady(true);
                                if (!arTutorialShownRef.current) {
                                    arTutorialShownRef.current = true;
                                    setShowARTutorial(true);
                                }
                            }}
                            onStatus={(s) => setArStatus(s)}
                        />
                        <HoloOverlay trackingRef={trackingRef} />
                        <ARTutorial />

                        {!arReady && (() => {
                            const isDark = resolvedTheme === 'dark';
                            const bg = isDark ? 'rgba(5, 5, 20, 0.93)' : 'rgba(240, 242, 255, 0.93)';
                            const heading = isDark ? '#e0e7ff' : '#1e1b4b';
                            const stepDimmed = isDark ? '#374151' : '#9ca3af';
                            const stepActive = isDark ? '#c7d2fe' : '#3730a3';
                            return (
                                <div style={{
                                    position: 'fixed', inset: 0, zIndex: 9999,
                                    display: 'flex', flexDirection: 'column',
                                    alignItems: 'center', justifyContent: 'center',
                                    background: bg,
                                    backdropFilter: 'blur(12px)',
                                }}>
                                    <div style={{
                                        width: 72, height: 72, borderRadius: '50%',
                                        border: '3px solid rgba(99,102,241,0.2)',
                                        borderTopColor: '#6366f1',
                                        borderRightColor: '#8b5cf6',
                                        animation: 'ar-spin 1s linear infinite',
                                        marginBottom: 32,
                                        boxShadow: '0 0 24px rgba(99,102,241,0.4)',
                                    }} />

                                    <p style={{
                                        color: heading, fontFamily: 'Inter,sans-serif',
                                        fontSize: 20, fontWeight: 700, letterSpacing: '0.04em',
                                        marginBottom: 24,
                                    }}>Initialising AR</p>

                                    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 320 }}>
                                        {STEPS.map((step, i) => {
                                            const done = i < currentIdx;
                                            const active = i === currentIdx;
                                            return (
                                                <div key={step} style={{
                                                    display: 'flex', alignItems: 'center', gap: 12,
                                                    opacity: i > currentIdx ? 0.3 : 1,
                                                    transition: 'opacity 0.4s',
                                                }}>
                                                    <span style={{
                                                        width: 20, height: 20, borderRadius: '50%',
                                                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                                                        background: done ? '#10b981' : active ? '#6366f1' : 'transparent',
                                                        border: done ? 'none' : active ? '2px solid #6366f1' : '2px solid rgba(99,102,241,0.3)',
                                                        animation: active ? 'ar-pulse 1s ease-in-out infinite' : 'none',
                                                        flexShrink: 0, fontSize: 11, color: '#fff',
                                                    }}>{done ? '✓' : ''}</span>
                                                    <span style={{
                                                        color: done ? '#10b981' : active ? stepActive : stepDimmed,
                                                        fontFamily: 'Inter,sans-serif', fontSize: 13,
                                                        fontWeight: active ? 600 : 400,
                                                    }}>{step}</span>
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            );
                        })()}

                        <style>{`
                            @keyframes ar-spin  { to { transform: rotate(360deg); } }
                            @keyframes ar-pulse { 0%,100% { box-shadow: 0 0 0 0 rgba(99,102,241,0.4); } 50% { box-shadow: 0 0 0 6px rgba(99,102,241,0); } }
                        `}</style>
                    </>
                );
            })()}
        </div>
    );
}
